/**
 * Mock data for the Leads pipeline
 *
 * Flow (from flow.md):
 *   Prospect (auto-ingest) → enrich → BusinessProfile
 *   Seller reviews → Lead created → MediaPlan generated → Campaign → Creative
 *
 * Lead statuses: new | qualified | active | closed
 *   new       = prospect ingested, not yet enriched
 *   qualified = enriched (BusinessProfile exists), ready for media plan
 *   active    = media plan generated, pursuing the deal
 *   closed    = won or lost
 */

import {
  Inbox, Eye, Zap, CheckCircle2,
} from 'lucide-react';

// ── Status config (4 statuses aligned to ER) ─────────────────────────────────

export const LEAD_STATUSES = [
  { key: 'new', label: 'New', color: 'slate', icon: Inbox, className: 'bg-slate-50 text-slate-600 border-slate-200' },
  { key: 'qualified', label: 'Qualified', color: 'amber', icon: Eye, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'active', label: 'Active', color: 'blue', icon: Zap, className: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'closed', label: 'Closed', color: 'emerald', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

// ── Feedback / outreach constants ────────────────────────────────────────────

export const DISMISS_REASONS = ['Not relevant', 'Wrong market', 'Too small', 'Duplicate', 'Other'];
export const LOST_REASONS = ['Budget constraints', 'Went with competitor', 'Not interested', 'Bad timing', 'Already has agency', 'Other'];
export const EMAIL_TONES = ['Professional', 'Friendly', 'Energetic'];

// ── Email templates ──────────────────────────────────────────────────────────

export const getEmailTemplate = (tone, businessName, triggerEvent) => {
  const templates = {
    Professional: `Subject: A CTV Ad Concept for ${businessName}\n\nDear ${businessName} Team,\n\nI noticed ${triggerEvent.toLowerCase()} and wanted to reach out with something concrete rather than just a pitch.\n\nOur team at iHeartMedia created a custom media plan specifically for ${businessName}. It outlines how your brand could reach local viewers on premium streaming platforms in your target market.\n\nYou can preview the plan using the link below. No commitment needed — I'd love to hear what you think and explore whether CTV advertising could complement your current radio strategy.\n\nWould you have 15 minutes this week for a quick call?\n\nBest regards`,
    Friendly: `Subject: We put together something cool for ${businessName}\n\nHey there!\n\nI saw that ${triggerEvent.toLowerCase()} — exciting stuff! It got me thinking about how ${businessName} could expand to streaming TV.\n\nSo our team went ahead and put together a custom media plan just for you. No strings attached — I just think you'll really like what we've mapped out for your market.\n\nCheck out the plan below and let me know what you think! If you're curious about running CTV alongside your radio campaigns, I'd love to chat.\n\nCheers`,
    Energetic: `Subject: ${businessName} on Streaming TV? Here's our plan!\n\nHi there!\n\nBig things are happening — ${triggerEvent.toLowerCase()} — and I think it's the perfect time to amplify your reach with Connected TV.\n\nWe created a custom media plan for ${businessName} that could reach thousands of streaming viewers in your area. The best part? It's already mapped out and ready to go.\n\nThis is just a taste of what's possible when you combine radio's reach with CTV's precision targeting. Let's make some noise together!\n\nGot 15 minutes this week? I'd love to walk you through the full opportunity.`,
  };
  return templates[tone] || templates.Professional;
};

// ── Mock leads (ER-aligned shape) ────────────────────────────────────────────

export const MOCK_LEADS = [
  {
    // ── Lead entity fields ──
    id: 'lead-001',
    businessProfileId: 'biz-kfire',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'qualified',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-04T10:00:00Z',
    updatedAt: '2026-03-04T12:00:00Z',

    // ── Prospect origin ──
    prospectId: 'psp-001',
    enrichmentStatus: 'enriched',

    // ── Denormalized from BusinessProfile ──
    businessName: 'KFire Korean BBQ - Logan Square',
    businessCategory: 'Korean Restaurant',
    businessAddress: '2528 N Milwaukee Ave, Chicago, IL 60647',
    businessPhone: '(773) 555-2528',
    businessWebsite: 'kfirebbq.com',
    businessRating: 4.6,
    businessReviewsCount: 4,
    businessPhotos: 34,

    // ── Trigger (from Prospect / media monitor) ──
    triggerEvent: 'New radio campaign on WBBM-FM',
    triggerDate: '2026-03-04T08:00:00Z',

    // ── Media plans (1:N) ──
    mediaPlans: [],
  },
  {
    id: 'lead-002',
    businessProfileId: 'bp-audi-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'qualified',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-04T07:00:00Z',
    updatedAt: '2026-03-04T10:00:00Z',

    prospectId: 'psp-002',
    enrichmentStatus: 'enriched',

    businessName: 'Audi Denver',
    businessCategory: 'Car Dealership',
    businessAddress: '6060 S Broadway, Littleton, CO 80121',
    businessPhone: '(303) 555-6060',
    businessWebsite: 'audidenver.com',
    businessRating: 4.6,
    businessReviewsCount: 5,
    businessPhotos: 88,

    triggerEvent: 'Increased radio spend detected',
    triggerDate: '2026-03-04T05:00:00Z',

    mediaPlans: [],
  },
  {
    id: 'lead-003',
    businessProfileId: 'bp-taspen-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'active',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-03T09:00:00Z',
    updatedAt: '2026-03-04T08:00:00Z',

    prospectId: 'psp-003',
    enrichmentStatus: 'enriched',

    businessName: 'Twin Aspen Dental Center',
    businessCategory: 'Dental Practice',
    businessAddress: '19572 Stroh Rd, Parker, CO 80134',
    businessPhone: '(303) 555-1957',
    businessWebsite: 'twinaspendental.com',
    businessRating: 5.0,
    businessReviewsCount: 5,
    businessPhotos: 18,

    triggerEvent: 'First-time radio advertiser',
    triggerDate: '2026-03-03T06:00:00Z',

    mediaPlans: [
      { id: 'mp-taspen-001', name: '30-Day CTV Launch — Parker, CO', budget: '$2,500', status: 'proposed', createdAt: '2026-03-03T12:00:00Z' },
    ],
  },
  {
    id: 'lead-004',
    businessProfileId: 'bp-courtyard-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'qualified',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-03T08:00:00Z',
    updatedAt: '2026-03-03T10:00:00Z',

    prospectId: 'psp-004',
    enrichmentStatus: 'enriching',

    businessName: 'Courtyard Social',
    businessCategory: 'Bar & Restaurant',
    businessAddress: '333 Perry St Ste. D, Castle Rock, CO 80104',
    businessPhone: '(303) 555-0333',
    businessWebsite: 'courtyardsocial.com',
    businessRating: 4.5,
    businessReviewsCount: 5,
    businessPhotos: 52,

    triggerEvent: 'New radio campaign on KOA',
    triggerDate: '2026-03-03T06:00:00Z',

    mediaPlans: [],
  },
  {
    id: 'lead-005',
    businessProfileId: 'bp-crossfit-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'new',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-02T14:00:00Z',
    updatedAt: '2026-03-02T14:00:00Z',

    prospectId: 'psp-005',
    enrichmentStatus: null,

    businessName: 'CrossFit Train',
    businessCategory: 'Fitness Center',
    businessAddress: '2031 Bryant St, Denver, CO 80211',
    businessPhone: '(303) 555-2031',
    businessWebsite: 'crossfittrain.com',
    businessRating: null,
    businessReviewsCount: null,
    businessPhotos: null,

    triggerEvent: 'Seasonal radio push detected',
    triggerDate: '2026-03-02T10:00:00Z',

    mediaPlans: [],
  },
  {
    id: 'lead-006',
    businessProfileId: 'bp-lizzano-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'new',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-02T12:00:00Z',
    updatedAt: '2026-03-02T12:00:00Z',

    prospectId: 'psp-006',
    enrichmentStatus: null,

    businessName: 'Lizzano Focacceria & Spritzeria',
    businessCategory: 'Italian Restaurant',
    businessAddress: '200 Michigan Ave, Chicago, IL 60601',
    businessPhone: '(312) 555-0200',
    businessWebsite: 'lizzanochicago.com',
    businessRating: null,
    businessReviewsCount: null,
    businessPhotos: null,

    triggerEvent: 'New radio campaign on WLS-AM',
    triggerDate: '2026-03-02T08:00:00Z',

    mediaPlans: [],
  },
  {
    id: 'lead-007',
    businessProfileId: 'bp-veteran-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'active',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-03-01T11:00:00Z',
    updatedAt: '2026-03-03T16:00:00Z',

    prospectId: 'psp-007',
    enrichmentStatus: 'enriched',

    businessName: 'Veteran Windows and Doors',
    businessCategory: 'Home Services',
    businessAddress: '360 S Navajo St, Denver, CO 80223',
    businessPhone: '(303) 555-0360',
    businessWebsite: 'veteranwindows.com',
    businessRating: 4.9,
    businessReviewsCount: 5,
    businessPhotos: 42,

    triggerEvent: 'Competitor dropped radio ads',
    triggerDate: '2026-03-01T08:00:00Z',

    mediaPlans: [
      { id: 'mp-veteran-001', name: '60-Day CTV Blitz — Denver Metro', budget: '$5,000', status: 'proposed', createdAt: '2026-03-02T10:00:00Z' },
    ],
  },
  {
    id: 'lead-008',
    businessProfileId: 'bp-fellowship-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'closed',
    closedReason: 'won',
    closedDetail: null,
    createdAt: '2026-02-28T09:00:00Z',
    updatedAt: '2026-03-02T14:00:00Z',

    prospectId: 'psp-008',
    enrichmentStatus: 'enriched',

    businessName: 'Fellowship Home Loans',
    businessCategory: 'Financial Services',
    businessAddress: '8951 Collin McKinney Pkwy, McKinney, TX 75070',
    businessPhone: '(469) 555-8951',
    businessWebsite: 'fellowshiphomeloans.com',
    businessRating: 4.4,
    businessReviewsCount: 4,
    businessPhotos: 12,

    triggerEvent: 'Increased radio spend detected',
    triggerDate: '2026-02-28T06:00:00Z',

    mediaPlans: [
      { id: 'mp-fellowship-001', name: '90-Day CTV — McKinney/Plano DMA', budget: '$4,200/mo', status: 'active', createdAt: '2026-02-28T14:00:00Z' },
    ],
    dealValue: '$4,200/mo',
  },
  {
    id: 'lead-009',
    businessProfileId: 'bp-rise-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'closed',
    closedReason: 'lost',
    closedDetail: 'Budget constraints',
    createdAt: '2026-02-27T10:00:00Z',
    updatedAt: '2026-03-01T11:00:00Z',

    prospectId: 'psp-009',
    enrichmentStatus: 'enriched',

    businessName: 'Rise and Shine Pediatric Dentistry',
    businessCategory: 'Pediatric Dental',
    businessAddress: '1780 S Bellaire St #655, Denver, CO 80222',
    businessPhone: '(303) 555-1780',
    businessWebsite: 'riseandshinedentistry.com',
    businessRating: 4.9,
    businessReviewsCount: 5,
    businessPhotos: 24,

    triggerEvent: 'First-time radio advertiser',
    triggerDate: '2026-02-27T06:00:00Z',

    mediaPlans: [
      { id: 'mp-rise-001', name: '30-Day CTV Trial — SE Denver', budget: '$1,800', status: 'proposed', createdAt: '2026-02-27T14:00:00Z' },
    ],
  },
  {
    id: 'lead-010',
    businessProfileId: 'bp-mensch-001',
    advertiserId: 'adv-001',
    createdByUserId: 'user-001',
    status: 'active',
    closedReason: null,
    closedDetail: null,
    createdAt: '2026-02-27T08:00:00Z',
    updatedAt: '2026-03-02T15:00:00Z',

    prospectId: 'psp-010',
    enrichmentStatus: 'enriched',

    businessName: "Mensch's Deli",
    businessCategory: 'Restaurant & Deli',
    businessAddress: '1608 Chicago Ave, Evanston, IL 60201',
    businessPhone: '(847) 555-1608',
    businessWebsite: 'menschsdeli.com',
    businessRating: 4.5,
    businessReviewsCount: 4,
    businessPhotos: 38,

    triggerEvent: 'Seasonal radio push detected',
    triggerDate: '2026-02-27T06:00:00Z',

    mediaPlans: [
      { id: 'mp-mensch-001', name: '30-Day Spring Campaign — Evanston', budget: '$2,000', status: 'proposed', createdAt: '2026-02-27T14:00:00Z' },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

export const getStatusConfig = (statusKey) => {
  return LEAD_STATUSES.find((s) => s.key === statusKey) || LEAD_STATUSES[0];
};

export const computeFunnelCounts = (leads) => {
  const counts = {};
  LEAD_STATUSES.forEach((s) => (counts[s.key] = 0));
  leads.forEach((l) => (counts[l.status] = (counts[l.status] || 0) + 1));
  return counts;
};

export const computeWinRate = (leads) => {
  const closed = leads.filter((l) => l.status === 'closed');
  if (closed.length === 0) return 0;
  const won = closed.filter((l) => l.closedReason === 'won').length;
  return Math.round((won / closed.length) * 100);
};

/**
 * Format a relative time string from an ISO date
 */
export const formatRelativeTime = (isoDate) => {
  if (!isoDate) return '';
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
};

/** Lead has been enriched (BusinessProfile fully populated) */
export const isEnriched = (lead) => lead.enrichmentStatus === 'enriched';

/** Lead is currently being enriched */
export const isEnriching = (lead) => lead.enrichmentStatus === 'enriching';

/** Lead has at least one associated media plan */
export const hasMediaPlan = (lead) => lead.mediaPlans?.length > 0;

// ── Queue helpers (for Work Queue grouping) ─────────────────────────────────

/**
 * Assign a lead to a queue section based on status and timing.
 * Returns: 'new_opportunities' | 'active_conversations' | 'needs_attention' | null
 */
export const getQueueSection = (lead) => {
  if (lead.status === 'closed') return null;
  if (lead.status === 'active') return 'active_conversations';
  const hoursSinceTrigger = (Date.now() - new Date(lead.triggerDate).getTime()) / (1000 * 60 * 60);
  if (hoursSinceTrigger < 48) return 'needs_attention';
  return 'new_opportunities';
};

/**
 * Compute a confidence level based on data completeness.
 * Returns: 'high' | 'medium' | 'low'
 */
export const getConfidence = (lead) => {
  if (lead.enrichmentStatus === 'enriched' && lead.businessRating >= 4.5) return 'high';
  if (lead.enrichmentStatus === 'enriched') return 'medium';
  return 'low';
};

/**
 * Get the recommended next action for a lead in the queue.
 * Returns: { type: string, label: string }
 */
export const getNextAction = (lead) => {
  if (lead.status === 'new' && !lead.enrichmentStatus) {
    return { type: 'start_deal', label: 'Start deal' };
  }
  if (lead.status === 'new' && lead.enrichmentStatus === 'enriching') {
    return { type: 'enriching', label: 'Enriching...' };
  }
  if (lead.status === 'qualified' && !hasMediaPlan(lead)) {
    return { type: 'start_deal', label: 'Build pitch' };
  }
  if (lead.status === 'active' && hasMediaPlan(lead)) {
    return { type: 'continue', label: 'Continue' };
  }
  return { type: 'view', label: 'View' };
};
